// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import classNames from 'classnames';
import {defineMessages, FormattedMessage} from 'react-intl';
import Button, {ButtonLink, ButtonGroup} from 'sail/Button';
import {useHighlightElement} from 'docs/hooks';

import {loadStripe} from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

import Box from 'sail/Box';
import Group from 'sail/Group';

import Text from 'sail/Text/Text';
import {Body} from 'sail/Text';

import {DemoContainer} from 'docs/components';
import {useAccount, useApi} from 'docs/data';
import {prefillStripeElement} from 'docs/util';

import './CardPaymentDemo.scss';

const messages = defineMessages({
  buttonLabel: {
    id: 'docsShell.CardPaymentDemo.buttonLabel',
    description: 'Label on a button asking users to pay with their card',
    defaultMessage: 'Pay',
  },
  successMessage: {
    id: 'docsShell.CardPaymentDemo.successMessage',
    description:
      'A message we display to the user after they complete the demo.',
    defaultMessage:
      "Success! Here's the PaymentIntent that was returned by the Stripe API. The status of succeeded indicates that the payment was completed successfully.",
  },
  restartLabel: {
    id: 'docsShell.CardPaymentDemo.restartLabel',
    description: 'A link that lets the user restart the demo',
    defaultMessage: 'Restart demo.',
  },
  prefillLabel: {
    id: 'docsShell.CardPaymentDemo.prefillLabel',
    description: 'A button that lets the user prefill card details',
    defaultMessage: 'Prefill card details',
  },
});
type PaymentIntent = {|
  [string]: any,
|};

type ResultComponentProps = {|
  result: PaymentIntent,
  setResult: (?PaymentIntent) => void,
|};

const Result = ({result, setResult}: ResultComponentProps) => {
  const ref = useHighlightElement();

  return (
    <>
      <Box flex={{justifyContent: 'space-between'}} flexGrow={1}>
        <Text color="dark" weight="medium" size={16} lineHeight={20}>
          Payment completed
        </Text>
        <ButtonGroup>
          <Button
            className="CardPaymentDemo-button"
            ghost
            size="small"
            label="Restart demo"
            onClick={() => {
              setResult(null);
            }}
          />
        </ButtonGroup>
      </Box>
      <Group
        spacing={12}
        flexGrow={2}
        flex={{justifyContent: 'center', alignItems: 'center'}}
      >
        <Box style={{textAlign: 'center'}} margin={{top: 24}}>
          <Body>
            Success! Here's the <code>PaymentIntent</code> that was returned by
            the Stripe API. The <code>status</code> of <code>succeeded</code>{' '}
            indicates that the payment was completed successfully.
          </Body>
        </Box>
        <Box className="CardPaymentDemo-result">
          <pre className="language-json CodeBlock">
            <code ref={ref} className="language-json">
              {JSON.stringify(result, null, 2)}
            </code>
          </pre>
        </Box>
      </Group>
    </>
  );
};

type DemoComponentProps = {|
  setResult: (?PaymentIntent) => void,
  newDesign?: boolean,
|};

const Demo = ({setResult, newDesign = false}: DemoComponentProps) => {
  const elements = useElements();
  const stripe = useStripe();
  const {api} = useApi();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  const pay = async () => {
    if (!stripe) {
      return;
    }
    setPending(true);

    const response = await api.createPaymentIntent({});

    if (response.clientSecret) {
      const payload = await stripe.confirmCardPayment(response.clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      setResult(payload.paymentIntent);
      setPending(false);
    } else {
      setPending(false);
      const domain = window.location.hostname || 'stripe.com';
      setError(
        domain.match('dev.stripe.me')
          ? 'This demo does not work locally.'
          : `An error occurred. We're looking into it!`,
      );
      setTimeout(() => {
        setError('');
      }, 2000);
    }
  };

  const prefillTestCard = () => {
    const cardElement = elements.getElement(CardElement);
    prefillStripeElement('cardNumber', '4000000000003220', cardElement);
    prefillStripeElement('cardExpiry', '0824', cardElement, 1000);
    prefillStripeElement('cardCvc', '123', cardElement, 1600);
    prefillStripeElement('postalCode', '94107', cardElement, 2400);
  };

  const cardStyle = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '14px',
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a',
      },
    },
  };

  return (
    <Group
      spacing={8}
      flexGrow={2}
      flex={{justifyContent: 'center', alignItems: 'center'}}
      padding={newDesign ? {} : {top: 32}}
      className={classNames(newDesign && 'CardPaymentDemo-new')}
    >
      <Box style={{textAlign: 'center'}}>
        <Box
          className={'CardPaymentDemo-element'}
          padding={{top: 12, left: 12, right: 12}}
        >
          <CardElement options={cardStyle} />
        </Box>
      </Box>
      <Group spacing={12} alignItems="center">
        <Button
          color="blue"
          type="submit"
          label={<FormattedMessage {...messages.buttonLabel} />}
          className="CardPaymentDemo-pay"
          onClick={pay}
          pending={pending}
          size={newDesign ? 'jumbo' : 'medium'}
        />
        <ButtonLink
          label={<FormattedMessage {...messages.prefillLabel} />}
          onClick={prefillTestCard}
        />
        {error && <Text>{error}</Text>}
      </Group>
    </Group>
  );
};

export const CardPaymentDemo = () => {
  const {account} = useAccount();
  const [promise, setPromise] = useState(null);

  useEffect(() => {
    setPromise(loadStripe(account.keys.publishable));
  }, [account.keys.publishable]);

  return (
    <Elements stripe={promise}>
      <DemoContainer
        demo={Demo}
        result={Result}
        name="accept-a-card-payment"
        integration="with-webhooks"
      />
    </Elements>
  );
};

const NewResult = ({result, setResult}: ResultComponentProps) => {
  const ref = useHighlightElement();

  return (
    <Group spacing={8} style={{width: '380px'}} className="Result">
      <Body color="gray">
        <FormattedMessage {...messages.successMessage} />{' '}
        <ButtonLink
          display="inline-flex"
          label={<FormattedMessage {...messages.restartLabel} />}
          onClick={() => setResult(null)}
        />
      </Body>
      <pre className="language-json CodeBlock">
        <code ref={ref} className="language-json">
          {JSON.stringify(result, null, 2)}
        </code>
      </pre>
    </Group>
  );
};

export const NewCardPaymentDemo = () => {
  const {account} = useAccount();
  const [promise, setPromise] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setPromise(loadStripe(account.keys.publishable));
  }, [account.keys.publishable]);

  return (
    <Elements stripe={promise}>
      {result ? (
        <NewResult result={result} setResult={setResult} />
      ) : (
        <Demo newDesign setResult={setResult} />
      )}
    </Elements>
  );
};
